import { useEffect, useState } from 'react';
import { EmptyState } from '../../../../components/EmptyState';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { StackedCell } from '../../../../components/table/StackedCell';
import { TextCell } from '../../../../components/table/TextCell';
import { formatMoney } from '../../../../lib/formatMoney';
import { ppasApi } from '../ppasApi';
import {
  PPA_EXPENSE_CLASS,
  PPA_EXPENSE_CLASS_LABEL,
  type PpaExpenditureLine,
} from '../ppaTypes';

/**
 * The plan's EXPENDITURES — every active budgeted line, under every department, at once.
 *
 * Captured from the reference's own `Expenditures` folder, which is a plan-level roll-up
 * grouped under the three expense classes. It answers the one question neither the department
 * table nor a PPA profile can: what the whole fiscal year is asking for, line by line, without
 * opening sixty PPAs.
 *
 * **A `<PreviewGrid>`, not a `<DataTablePanel>`**, and that is the shell test applied rather
 * than a size judgement: *does this collection get WRITTEN from where it is drawn?* It does
 * not — nothing here creates, corrects, deletes or restores a line, and every one of them is
 * written on the PPA profile its row names. A panel would bring search, sort, paging, a
 * selection column and a kebab, all of them chrome over rows that write nothing.
 *
 * **Each class owns its own TOTAL, under its own grid** — a `<PreviewTable
 * variant="table-display">` below the rows it adds up, which is the SALN folder's shape and
 * the one the PPA profile's expense-class tabs already take. The plan's grand total leads,
 * because a reader opening this tab is asking the whole-plan question first.
 *
 * **It fetches on MOUNT, and the tab is what mounts it.** The panel renders only while its tab
 * is active, so a reader who never opens Expenditures never pays for the query — the profile's
 * own lazy rule, at tab scale. Leaving and returning re-fetches, which is the stated cost.
 */
export function PlanExpendituresPanel({ planId }: { planId: string }) {
  const [lines, setLines] = useState<PpaExpenditureLine[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    setLines(null);
    setFailed(false);

    ppasApi
      .expenditures(planId, controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setLines(rows);
      })
      .catch(() => {
        if (controller.signal.aborted) return;

        // A failed roll-up says so rather than reading as an empty plan — the two are
        // different facts, and an empty grid would state the second.
        setFailed(true);
        setLines([]);
      });

    return () => controller.abort();
  }, [planId]);

  if (lines === null) {
    // The skeleton is the SHAPE of what it replaces — a total strip over three grouped
    // tables, not a generic block.
    return (
      <>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </>
    );
  }

  if (failed) {
    return (
      <EmptyState message="The expenditures could not be loaded. Nothing about the plan has changed — try opening this tab again." />
    );
  }

  const grandTotal = lines.reduce((sum, line) => sum + line.amount, 0);

  return (
    <>
      {/*
        THE PLAN'S OWN TOTAL leads, stated once. Each class states its own under its grid, and
        no grid restates the plan's — one figure, one place.
      */}
      <ProfileCard id="plan-expenditures-total" title="Expenditures">
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Budgeted lines', value: String(lines.length), mono: true },
            { label: 'Total', value: formatMoney(grandTotal), mono: true },
          ]}
        />
      </ProfileCard>

      {/*
        THE THREE CLASSES ALWAYS RENDER, in the government's own order, whether or not a class
        holds anything. A class that vanished when it emptied would make the tab a different
        shape per plan, and "nothing is budgeted for capital outlay" is exactly what a reader
        of a fiscal year's expenditures is checking for.
      */}
      {EXPENSE_ORDER.map((category) => {
        const group = lines.filter((line) => line.category === category);
        const subtotal = group.reduce((sum, line) => sum + line.amount, 0);

        return (
          <ProfileCard
            key={category}
            id={`plan-expenditures-${category}`}
            title={PPA_EXPENSE_CLASS_LABEL[category] ?? 'Unclassified'}
          >
            <div className="flex min-w-0 flex-col gap-3 p-4">
              <PreviewGrid<PpaExpenditureLine>
                columns={COLUMNS}
                rows={group}
                rowKey={(line) => line.id}
                empty="No department has budgeted anything under this expense class."
              />

              {/*
                The class's own total, BELOW the rows it adds up — a read-only fact being
                shown rather than a value being asked for, which is the test that separates
                the two treatments.
              */}
              <PreviewTable
                variant="table-display"
                rows={[{ label: 'Total', value: formatMoney(subtotal), mono: true }]}
              />
            </div>
          </ProfileCard>
        );
      })}
    </>
  );
}

/** The government's own order, which is the order the three folders read everywhere here. */
const EXPENSE_ORDER = [
  PPA_EXPENSE_CLASS.personnelServices,
  PPA_EXPENSE_CLASS.mooe,
  PPA_EXPENSE_CLASS.capitalOutlay,
] as const;

const COLUMNS = [
  {
    /*
      THE DEPARTMENT LEADS, because that is the band the reference draws down the left of this
      table. It repeats per row rather than merging — a `<PreviewGrid>` cannot row-span, which
      is the same shape difference the Ceiling band already carries one screen along.
    */
    header: 'Department',
    width: 200,
    render: (line: PpaExpenditureLine) => <TextCell value={line.orgUnitName} />,
  },
  {
    /*
      STACKED — the PPA's name over its number, which is the reference's own cell. A column of
      `PPA-2026-0007` identifies nothing to anyone who has not memorised the sequence, and the
      name alone cannot be quoted. `<StackedCell secondary>` is refused inside an index table
      and legal here: the dev error is scoped by `IndexTableSurfaceContext`, which a preview
      does not provide.
    */
    header: 'PPA',
    width: 240,
    render: (line: PpaExpenditureLine) => (
      <StackedCell primary={line.ppaName} secondary={line.ppaNumber} />
    ),
  },
  {
    header: 'Item',
    width: 260,
    render: (line: PpaExpenditureLine) => <TextCell value={line.item} />,
  },
  {
    header: 'Account Code',
    width: 160,
    // A code is a machine value, so it reads in the mono face like every other one.
    render: (line: PpaExpenditureLine) => <TextCell value={line.accountCode} mono />,
  },
  {
    header: 'Amount',
    width: 150,
    align: 'right' as const,
    render: (line: PpaExpenditureLine) => (
      <TextCell value={formatMoney(line.amount)} mono numeric />
    ),
  },
];
